// wechat.js
import wx from 'weixin-js-sdk';
import {getWxAuth, getSchemeCode} from '@/api/comm';
import store from '@/store';

const wechat = {
    wxAppId: "wx2172d3c1c1d92eb5", // 正式环境公众号ID
    isDebug: false, // 是否开启调试模式

    isWechat: false,              // 是否微信环境
    isConfiguring: false,         // 当前是否正在配置中，防止重复调用
    hasConfigured: false,         // 是否已经成功配置过微信SDK

    retryCount: 0,                // 当前重试次数
    maxRetry: 3,                  // 最大重试次数
    lastErrorTime: 0,             // 最近一次错误处理的时间（用于节流）
    throttleInterval: 1500,       // 错误处理节流间隔（毫秒）
    _hasListener: false,          // 是否已绑定全局错误监听

    // 默认配置参数，允许外部传入覆盖
    defaultOptions: {
        jsApiList: [
            'checkJsApi',
            'updateAppMessageShareData',    // 分享给朋友
            'updateTimelineShareData',      // 分享到朋友圈
            'onMenuShareTimeline',          // 分享到朋友圈（旧版）
            'onMenuShareAppMessage'         // 分享给朋友（旧版）
        ],
        openTagList: ['wx-open-launch-weapp'], // 需要使用的开放标签列表
    },

    /**
     * 初始化微信SDK配置
     * @param {Boolean} refresh - 是否强制刷新签名（默认 false）
     * @param {Object} options - 配置选项，包含 jsApiList 和 openTagList（默认使用 defaultOptions）
     */
    async init(refresh = false, options = this.defaultOptions) {
        if (this.isConfiguring) {
            this.debugLog('配置中，跳过重复调用');
            return;
        }
        this.isConfiguring = true;

        // 判断是否微信环境
        const ua = navigator.userAgent.toLowerCase();
        this.isWechat = /micromessenger/.test(ua);
        store.commit('SET_IS_WECHAT', this.isWechat);
        if (!this.isWechat) {
            this.isConfiguring = false;
            return;
        }

        try {
            // 获取页面干净URL，兼容iOS首次进入
            const url = this._getCleanUrl();

            // 缓存key和缓存时间
            const cacheKey = `wx_sign_${url}`;
            const cachedTime = sessionStorage.getItem(`${cacheKey}_ts`);
            const now = Date.now();
            const REFRESH_TIME = 25 * 60 * 1000; // 25分钟缓存过期时间

            // 如果有缓存且未过期且不强制刷新，直接跳过签名请求
            // if (
            //     sessionStorage.getItem(cacheKey) &&
            //     cachedTime &&
            //     now - Number(cachedTime) < REFRESH_TIME &&
            //     !refresh
            // ) {
            //     this.debugLog('命中缓存，跳过签名请求');
            //     this.isConfiguring = false;
            //     this.hasConfigured = true;
            //     return;
            // }

            // 请求后端获取微信签名数据
            this.openLoading();
            const response = await getWxAuth({url});
            const {timestamp, noncestr, signature} = response?.data || {};
            const appId = this.wxAppId; // 公众号AppId

            if (!appId || !timestamp || !noncestr || !signature) {
                this.closeLoading();
                throw new Error('缺少签名参数');
            }

            // 调用微信SDK配置
            await this._configWxSdk({
                appId,
                timestamp,
                nonceStr: noncestr,
                signature,
                jsApiList: ['checkJsApi', ...(options.jsApiList || [])],
                openTagList: ['wx-open-launch-weapp', ...(options.openTagList || [])],
            });

            // 配置成功，缓存签名状态和时间戳
            sessionStorage.setItem(cacheKey, '1');
            sessionStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
            this.retryCount = 0; // 成功后重置重试计数
            this.hasConfigured = true;
        } catch (error) {
            console.error('[wechat] 初始化失败:', error);
            // this._retryOnError(); // 失败时尝试重试 （弃用，微信不允许连续config）
        } finally {
            this.isConfiguring = false;
            this._bindErrorListener(); // 绑定全局错误监听（只绑定一次）
        }
    },

    /**
     * 调用微信SDK wx.config 进行配置，返回 Promise
     * @param {Object} config - wx.config 需要的配置参数
     * @returns {Promise}
     */
    async _configWxSdk(config) {
        this.debugLog('wx.config 参数', config);

        return new Promise((resolve, reject) => {
            let errorTriggered = false;     // 错误触发标志
            let resolveCalled = false;      // resolve 是否已调用
            let rejectCalled = false;       // reject 是否已调用

            wx.config({
                debug: this.isDebug,
                ...config
            })

            wx.ready(() => {
                console.log('[wechat] wx.ready arrive');
                this.closeLoading();
                if (errorTriggered) {
                    this.debugLog('wx.ready 到达，但之前已有错误，忽略 ready');
                    return;
                }
                if (!resolveCalled) {
                    resolveCalled = true;
                    this.debugLog('wx.ready 成功');
                    resolve();
                }
            });

            wx.error((err) => {
                console.log('[wechat] wx.error arrive', err);
                this.closeLoading();
                errorTriggered = true;
                if (!rejectCalled) {
                    // 如果 ready 已经成功过，需要撤销成功，执行 reject
                    if (resolveCalled) {
                        rejectCalled = true;
                        this._clearCache(); // 清理缓存
                        this.debugLog('wx.error 失败，撤销之前的成功，reject');
                        reject(err);
                        return;
                    }
                    rejectCalled = true;
                    this.debugLog('wx.error 失败，直接 reject');
                    reject(err);
                }
            });
        });
    },

    /**
     * 获取当前页面的“干净”URL（去除hash），兼容iOS首入URL缓存
     * @returns {string}
     */
    _getCleanUrl() {
        let url = location.href.split('#')[0];
        const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const stored = sessionStorage.getItem('firstEntryUrl');
        console.log('isIos', isIos, navigator.userAgent)
        console.log('stored-firstEntryUrl', stored)
        if (isIos && stored) {
            url = stored.split('#')[0];
        }

        return url;
    },

    /**
     * 出错时的重试逻辑，最多重试 maxRetry 次
     */
    _retryOnError() {
        if (this.retryCount >= this.maxRetry) {
            console.warn('[wechat] 重试达到最大次数，停止重试');
            return;
        }

        this.retryCount++;
        console.log(`[wechat] 发生错误，重试第 ${this.retryCount} 次`);
        setTimeout(() => {
            this.init(true);
        }, 1500);
    },

    /**
     * 绑定微信开放标签全局错误监听，节流处理，防止重复绑定
     */
    _bindErrorListener() {
        if (this._hasListener) return;

        document.addEventListener('WeixinOpenTagsError', this._onOpenTagsError.bind(this));
        this._hasListener = true;
    },

    /**
     * 处理微信开放标签的错误事件，根据错误类型触发重试
     * @param {Event} e
     */
    _onOpenTagsError(e) {
        const now = Date.now();
        if (now - this.lastErrorTime < this.throttleInterval) return; // 1.5秒内节流
        this.lastErrorTime = now;

        const errMsg = e.detail?.errMsg || '未知错误';
        console.error('[wechat] WeixinOpenTagsError:', errMsg);

        // 根据错误内容判断是否需要重试
        if (
            errMsg.includes('config:invalid signature') ||
            errMsg.includes('signature error') ||
            errMsg.includes('config:fail')
        ) {
            // this._retryOnError();  // （弃用，微信不允许连续config）
        }
    },

    /**
     * 清除缓存，用于重新初始化
     */
    _clearCache() {
        const url = this._getCleanUrl();
        const cacheKey = `wx_sign_${url}`;
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(`${cacheKey}_ts`);
    },

    /**
     * 调试日志，只在非生产环境打印
     * @param  {...any} args
     */
    debugLog(...args) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[wechat]', ...args);
        }
    },

    /**
     * 清空所有缓存，用于清理缓存
     */
    _clearAllCache() {
        // 这里遍历 sessionStorage 中所有 key，找到以 wx_sign_ 开头的都清理
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('wx_sign_')) {
                sessionStorage.removeItem(key);
                // 因为有_ts后缀的也要删
                const tsKey = `${key}_ts`;
                if (sessionStorage.getItem(tsKey)) {
                    sessionStorage.removeItem(tsKey);
                }
                // 重新遍历长度可能变化，索引回退一个
                i--;
            }
        }
    },

    /**
     * 打开loading
     */
    openLoading() {
        store.commit("SET_WX_CHAT_LOADING", true);
    },

    /**
     * 关闭loading
     */
    closeLoading() {
        store.commit("SET_WX_CHAT_LOADING", false);
    },

    /**
     * 设置分享给朋友
     * @param {Object} shareData - 分享数据
     * @param {string} shareData.title - 分享标题
     * @param {string} shareData.desc - 分享描述
     * @param {string} shareData.link - 分享链接
     * @param {string} shareData.imgUrl - 分享图片
     * @param {Function} shareData.success - 分享成功回调
     * @param {Function} shareData.fail - 分享失败回调
     */
    setAppMessageShare(shareData) {
        if (!this.isWechat || !this.hasConfigured) {
            console.warn('[wechat] 非微信环境或未配置，无法设置分享');
            return;
        }

        // 新版分享接口
        wx.updateAppMessageShareData({
            title: shareData.title || '',
            desc: shareData.desc || '',
            link: shareData.link || location.href,
            imgUrl: shareData.imgUrl || '',
            success: () => {
                console.log('[wechat] 分享给朋友设置成功');
                shareData.success && shareData.success();
            },
            fail: (err) => {
                console.error('[wechat] 分享给朋友设置失败:', err);
                shareData.fail && shareData.fail(err);
            }
        });

        // 旧版分享接口（兼容）
        wx.onMenuShareAppMessage({
            title: shareData.title || '',
            desc: shareData.desc || '',
            link: shareData.link || location.href,
            imgUrl: shareData.imgUrl || '',
            success: () => {
                console.log('[wechat] 分享给朋友设置成功（旧版）');
                shareData.success && shareData.success();
            },
            fail: (err) => {
                console.error('[wechat] 分享给朋友设置失败（旧版）:', err);
                shareData.fail && shareData.fail(err);
            }
        });
    },

    /**
     * 设置分享到朋友圈
     * @param {Object} shareData - 分享数据
     * @param {string} shareData.title - 分享标题
     * @param {string} shareData.link - 分享链接
     * @param {string} shareData.imgUrl - 分享图片
     * @param {Function} shareData.success - 分享成功回调
     * @param {Function} shareData.fail - 分享失败回调
     */
    setTimelineShare(shareData) {
        if (!this.isWechat || !this.hasConfigured) {
            console.warn('[wechat] 非微信环境或未配置，无法设置分享');
            return;
        }

        // 新版分享接口
        wx.updateTimelineShareData({
            title: shareData.title || '',
            link: shareData.link || location.href,
            imgUrl: shareData.imgUrl || '',
            success: () => {
                console.log('[wechat] 分享到朋友圈设置成功');
                shareData.success && shareData.success();
            },
            fail: (err) => {
                console.error('[wechat] 分享到朋友圈设置失败:', err);
                shareData.fail && shareData.fail(err);
            }
        });

        // 旧版分享接口（兼容）
        wx.onMenuShareTimeline({
            title: shareData.title || '',
            link: shareData.link || location.href,
            imgUrl: shareData.imgUrl || '',
            success: () => {
                console.log('[wechat] 分享到朋友圈设置成功（旧版）');
                shareData.success && shareData.success();
            },
            fail: (err) => {
                console.error('[wechat] 分享到朋友圈设置失败（旧版）:', err);
                shareData.fail && shareData.fail(err);
            }
        });
    },

    /**
     * 设置分享（同时设置分享给朋友和分享到朋友圈）
     * @param {Object} shareData - 分享数据
     * @param {string} shareData.title - 分享标题
     * @param {string} shareData.desc - 分享描述（仅分享给朋友时使用）
     * @param {string} shareData.link - 分享链接
     * @param {string} shareData.imgUrl - 分享图片
     * @param {Function} shareData.success - 分享成功回调
     * @param {Function} shareData.fail - 分享失败回调
     */
    setShare(shareData) {
        this.setAppMessageShare(shareData);
        this.setTimelineShare(shareData);
    },

    /**
     * 检测是否为微信环境
     * @returns {Boolean}
     */
    checkWechatEnvironment() {
        const ua = navigator.userAgent.toLowerCase();
        this.isWechat = /micromessenger/.test(ua);
        store.commit('SET_IS_WECHAT', this.isWechat);
        return this.isWechat;
    },

    /**
     * 获取非微信浏览器环境下的Scheme链接
     * @param {Object} params - 参数对象
     * @param {string} params.path - 跳转路径
     * @returns {Promise<string>} 返回Scheme链接
     */
    async getSchemeLink(params) {
        try {
            const response = await getSchemeCode(params);
            if (response.code === 200 && response.data) {
                return response.data;
            } else {
                throw new Error(response.msg || '获取Scheme链接失败');
            }
        } catch (error) {
            console.error('[wechat] 获取Scheme链接失败:', error);
            throw error;
        }
    },

    /**
     * 在非微信浏览器环境下跳转到小程序
     * @param {Object} params - 跳转参数
     * @param {string} params.path - 跳转路径
     * @param {Function} params.onError - 错误回调
     */
    async jumpToMiniApp(params) {
        const { path, onError } = params;
        
        // 如果是微信环境，直接返回，让微信组件处理
        if (this.checkWechatEnvironment()) {
            return;
        }

        try {
            // 分解成链接和参数
            const [link, query] = path.split('?');

            // 获取Scheme链接，只传递path参数
            let schemeUrl = await this.getSchemeLink({
                path: link || ''
            });

            // 从schemeUrl中提取cq参数值作为code
            let code = '';
            // 接口一定会有cq返回，直接提取
            const cqMatch = schemeUrl.match(/&cq=([^&]*)/);
            if (cqMatch && cqMatch[1]) {
                code = decodeURIComponent(cqMatch[1]);
                this.debugLog('从schemeUrl提取的code:', code);
            }

            // 构建新的参数对象
            let newParams = {};
            
            // 将提取的code添加到参数中
            if (code) {
                newParams.code = code;
            }
            
            // 如果有原始query参数，解析并添加到新参数中
            if (query) {
                try {
                    // 尝试解析query字符串为对象
                    const urlParams = new URLSearchParams(query);
                    for (let [key, value] of urlParams.entries()) {
                        newParams[key] = value;
                    }
                } catch (e) {
                    // 如果解析失败，直接作为字符串处理
                    newParams.query = query;
                }
            }

            // 将参数对象转换为JSON字符串并加密
            let cqValue = '';
            if (Object.keys(newParams).length > 0) {
                const paramsJson = JSON.stringify(newParams);
                this.debugLog('构建的参数对象:', newParams);
                this.debugLog('参数JSON字符串:', paramsJson);
                
                // 这里可以添加加密逻辑，暂时使用base64编码作为示例
                // 如果需要其他加密方式，请替换这里的实现
                cqValue = btoa(encodeURIComponent(paramsJson));
                
                // 验证长度限制（256字符）
                if (cqValue.length > 256) {
                    console.warn('[wechat] 加密后的cq参数长度超过256字符限制:', cqValue.length);
                    // 截取前256个字符
                    cqValue = cqValue.substring(0, 256);
                }
                
                this.debugLog('加密后的cq值:', cqValue);
            }

            // 重新构建schemeUrl，替换cq参数
            if (cqValue) {
                // 移除原有的cq参数
                schemeUrl = schemeUrl.replace(/&cq=[^&]*/, '');
                // 添加新的cq参数
                schemeUrl += `&cq=${encodeURIComponent(cqValue)}`;
            }

            console.log('最终schemeUrl', schemeUrl, '原始path:', path, '提取的code:', code)

            if (!schemeUrl) {
                throw new Error('获取Scheme链接失败');
            }

            // 尝试跳转
            this.triggerSchemeJump(schemeUrl, {
                onError
            });

        } catch (error) {
            console.error('[wechat] 跳转小程序失败:', error);
            if (onError) {
                onError(error);
            }
        }
    },

    /**
     * 触发Scheme跳转
     * @param {string} schemeUrl - Scheme链接
     * @param {Object} callbacks - 回调函数
     */
    triggerSchemeJump(schemeUrl, callbacks) {
        // 统一的Scheme跳转处理，不监听跳转结果
        this.universalSchemeJump(schemeUrl, callbacks);
    },

    /**
     * 通用Scheme跳转处理（适用于所有平台）
     * @param {string} schemeUrl - Scheme链接
     * @param {Object} callbacks - 回调函数
     */
    universalSchemeJump(schemeUrl, callbacks) {
        const { onError } = callbacks;
        
        // 直接跳转，不监听结果
        try {
            this.debugLog('开始 URL Scheme 跳转:', schemeUrl);
            window.location.href = schemeUrl;
        } catch (error) {
            console.error('[wechat] URL Scheme 跳转失败:', error);
            if (onError) {
                onError(error);
            }
        }
    },

    /**
     * 增强的 URL Scheme 跳转监听
     * @param {string} schemeUrl - Scheme链接
     * @param {Object} options - 配置选项
     * @param {boolean} options.autoRedirect - 是否自动跳转，默认true
     * @param {Function} options.onError - 错误回调
     */
    enhancedSchemeJump(schemeUrl, options = {}) {
        const {
            autoRedirect = true,
            onError
        } = options;

        // 自动跳转
        if (autoRedirect) {
            try {
                this.debugLog('开始增强 URL Scheme 跳转:', schemeUrl);
                window.location.href = schemeUrl;
            } catch (error) {
                console.error('[wechat] 增强 URL Scheme 跳转失败:', error);
                if (onError) {
                    onError(error);
                }
            }
        }
    },

    /**
     * 检测 URL Scheme 是否可用
     * @param {string} schemeUrl - Scheme链接
     * @returns {Promise<boolean>} 是否可用
     */
    async checkSchemeAvailability(schemeUrl) {
        // 简化版本：直接返回 true，因为无法准确检测
        // 如果需要检测，建议在应用端处理
        this.debugLog('检测 URL Scheme 可用性:', schemeUrl);
        return Promise.resolve(true);
    }
};

export default wechat;
